import { useEffect } from 'react';
import { getStoredConsent } from '@/lib/cookie-consent';

/**
 * Loads optional third-party scripts (Hotjar, Crisp) at runtime, only when
 * both a site key is configured AND the visitor has opted in to the
 * appropriate cookie category. Silent no-op until env vars are set.
 *
 * Required env vars (set in Vercel → Project → Settings → Environment):
 *   VITE_HOTJAR_ID          — Hotjar site ID (numeric)
 *   VITE_HOTJAR_SV          — Hotjar snippet version (e.g. "6")
 *   VITE_CRISP_WEBSITE_ID   — Crisp website ID (UUID)
 *
 * Consent gating:
 *   - Hotjar → requires `analytics` consent
 *   - Crisp  → requires `functional` consent (live chat persists prefs/cookies)
 *
 * The component also re-evaluates consent on the `bf-consent-change` event
 * so scripts load immediately when the visitor accepts after initial paint.
 */
const ThirdPartyScripts = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const loadScripts = () => {
      const consent = getStoredConsent();
      if (!consent) return;

      const hotjarId = import.meta.env.VITE_HOTJAR_ID as string | undefined;
      const hotjarSv = (import.meta.env.VITE_HOTJAR_SV as string | undefined) ?? '6';
      const crispId = import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined;

      // Hotjar
      if (hotjarId && consent.preferences.analytics && !document.getElementById('bf-hotjar')) {
        const script = document.createElement('script');
        script.id = 'bf-hotjar';
        script.async = true;
        script.text = `
          (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:${hotjarId},hjsv:${hotjarSv}};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
        `;
        document.head.appendChild(script);
      }

      // Crisp — live chat. Loads a lightweight stub then async-loads the full
      // widget. Respects functional-consent preference.
      if (crispId && consent.preferences.functional && !document.getElementById('bf-crisp')) {
        const w = window as unknown as {
          $crisp?: unknown[];
          CRISP_WEBSITE_ID?: string;
        };
        w.$crisp = [];
        w.CRISP_WEBSITE_ID = crispId;
        const script = document.createElement('script');
        script.id = 'bf-crisp';
        script.async = true;
        script.src = 'https://client.crisp.chat/l.js';
        document.head.appendChild(script);
      }
    };

    loadScripts();
    window.addEventListener('bf-consent-change', loadScripts);
    return () => window.removeEventListener('bf-consent-change', loadScripts);
  }, []);

  return null;
};

export default ThirdPartyScripts;
